import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ClipboardEdit, Download, FileText, Eye } from 'lucide-react';
import {
  docTitle,
  formatNumber,
  proposalDepositBalance,
  proposalDisplayStatus,
} from './badgeUtils';
import { getProposalInvoicingStatus } from '../../../utils/proposalInvoicing';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'ready', label: 'Ready For Approval' },
  { key: 'amountDue', label: 'Amount Due' },
  { key: 'declined', label: 'Declined' },
  { key: 'invoiced', label: 'Invoiced' },
];

export default function ProposalsTab({ proposals, projectId, canApprove }) {
  const [filter, setFilter] = useState('all');

  const visible = useMemo(
    () => proposals.filter((p) => !p.sortKey?.endsWith('#counter')),
    [proposals]
  );

  const stats = useMemo(() => {
    let totalApproved = 0;
    let totalProposed = 0;
    for (const p of visible) {
      const total = Number(p.totals?.grandTotal || 0);
      totalProposed += total;
      if (p.status === 'approved') totalApproved += total;
    }
    return { totalApproved, totalProposed };
  }, [visible]);

  const filtered = useMemo(() => {
    if (filter === 'all') return visible;
    return visible.filter((p) => {
      if (filter === 'ready') return p.status === 'sent';
      if (filter === 'declined') return p.status === 'declined';
      if (filter === 'invoiced') return p.status === 'approved' || getProposalInvoicingStatus(p) != null;
      if (filter === 'amountDue') return proposalDepositBalance(p) > 0;
      return true;
    });
  }, [visible, filter]);

  if (visible.length === 0) {
    return <p className="cp-empty">No proposals yet.</p>;
  }

  return (
    <div>
      <div className="cp-pill-row">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`cp-pill ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="cp-stats-card">
        <div className="cp-stat-item">
          <div className="cp-stat-icon cp-stat-icon-gray">
            <CheckCircle2 size={19} />
          </div>
          <div>
            <div className="cp-stat-value">{formatNumber(stats.totalApproved)}</div>
            <div className="cp-stat-label">Total Approved</div>
          </div>
        </div>
        <div className="cp-stat-item">
          <div className="cp-stat-icon cp-stat-icon-tan">
            <ClipboardEdit size={19} />
          </div>
          <div>
            <div className="cp-stat-value">{formatNumber(stats.totalProposed)}</div>
            <div className="cp-stat-label">Total Proposed</div>
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="cp-empty">No proposals match this filter.</p>
      ) : (
        <div className="cp-doc-list">
          {filtered.map((p) => {
            const depositBalance = proposalDepositBalance(p);
            const badge = proposalDisplayStatus(p, canApprove);
            const lineItems = Array.isArray(p.lineItems) ? p.lineItems : [];
            const itemCount = lineItems.length;
            const title =
              p.title ||
              docTitle(lineItems, p.proposalNumber ? `Proposal ${p.proposalNumber}` : 'Proposal');
            const room = itemCount > 0 ? lineItems[0]?.room || '' : '';
            const href = p.shareToken
              ? `/public/proposal/${p.shareToken}`
              : `/client/projects/${projectId}/proposals/${p.proposalId}`;
            const isExternal = Boolean(p.shareToken);

            const inner = (
              <>
                <div className="cp-doc-icon">
                  <FileText size={22} />
                </div>

                <div className="cp-doc-body">
                  <div className="cp-doc-number-row">
                    <span>{p.proposalNumber || 'Proposal'}</span>
                    <Download size={12} />
                  </div>
                  <p className="cp-doc-title">{title}</p>
                  <div className="cp-doc-meta-row">
                    {p.createdAt && <span>Date: {new Date(p.createdAt).toLocaleDateString()}</span>}
                    <span>Items: {itemCount}</span>
                    {room && <span>Room: {room}</span>}
                  </div>
                </div>

                <div className="cp-doc-amounts">
                  <div className="cp-doc-amount-col">
                    <div className="cp-doc-amount-value">{formatNumber(p.totals?.grandTotal)}</div>
                    <div className="cp-doc-amount-label">Proposed</div>
                  </div>
                  <div className="cp-doc-amount-col">
                    <div className="cp-doc-amount-value">{formatNumber(depositBalance)}</div>
                    <div className="cp-doc-amount-label">Deposit Balance</div>
                  </div>
                </div>

                <div className="cp-doc-status">
                  <span className={`cp-badge ${badge.cls}`}>{badge.label}</span>
                  <span className="cp-btn-primary" style={{ padding: '7px 16px', fontSize: 13 }}>
                    <Eye size={13} /> View
                  </span>
                </div>
              </>
            );

            return isExternal ? (
              <a
                key={p.proposalId}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="cp-doc-card"
              >
                {inner}
              </a>
            ) : (
              <Link key={p.proposalId} to={href} className="cp-doc-card">
                {inner}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
